import axios from "axios";
import MeasurementModel from "../models/MeasurementModel";
import MeasurementRowModel from "../models/MeasurementRowModel";
import WBSModel from "../models/WBSModel";
import MeasurementRowRepository from "../repositories/MeasurementRowRepository";
import MeasurementRowListService from "./MeasurementRowListService";
import WBSService, { maxRowsAtSnapshot } from "./WBSService";

/**
 * Service de Medições
 * @category Measurements
 * @hideconstructor
 */
class MeasurementRowService extends WBSService<MeasurementRowModel> {
  async deleteMeasurementRows(
    measurement: MeasurementModel,
    rows: MeasurementRowModel[]
  ) {
    return MeasurementRowRepository.deleteMeasurementRows(
      measurement.contractUid,
      measurement.uid
    ).then(() => {
      MeasurementRowListService.subtractValuesFromContractRow(rows);
    });
  }

  async updateMeasurementRows(
    measurement: MeasurementModel,
    rows: MeasurementRowModel[]
  ): Promise<void> {
    const rowsData = rows.map((row) => ({
      ...row.toSave(),
      uid: row.uid,
    }));

    const data = {
      contractUid: measurement.contractUid,
      measurementUid: measurement.uid,
      data: rowsData,
    };

    return axios
      .post(`${process.env.REACT_APP_UPDATE_MENSUREMENT_ROWS}`, data)
      .then(() => {
        rows.forEach((row) => row.setRowStatusToSaved());
      })
      .catch((err) => {
        console.error("Error on update measurements rows:", err);
        throw err;
      });
  }

  async getMeasurementRows(
    contractUid: string,
    measurementUid: string
  ): Promise<MeasurementRowModel[]> {
    return MeasurementRowRepository.getAllRowsFromDataBase(
      contractUid,
      measurementUid
    );
  }

  async fetchAllMeasurementsRows(
    contractUid: string
  ): Promise<{ uid: string; wbs: MeasurementRowModel[] }[]> {
    if (!contractUid) return [];
    return MeasurementRowRepository.fetchAllMeasurementsRows(contractUid);
  }

  async fetchMeasurementsRows(
    contractUid: string,
    measurementsUids: string[]
  ): Promise<{ uid: string; wbs: MeasurementRowModel[] }[]> {
    if (!contractUid || !measurementsUids?.length) return [];

    const promises = measurementsUids.map((measurementUid) => {
      return MeasurementRowRepository.fetchMeasurementRows(
        contractUid,
        measurementUid
      ).then((wbs) => ({ uid: measurementUid, wbs }));
    });

    return await Promise.all(promises);
  }

  subscribeMeasurementRows(
    contractUid: string,
    measurementUid: string,
    list: WBSModel<MeasurementRowModel>,
    onFinish: Function
  ) {
    if (!list || !contractUid || !measurementUid) return;
    if (list.fetchRowsIsComplete) return;

    try {
      const onCreate = (rows, isFinishing) => {
        if (rows.length && !list.fetchRowsIsComplete) {
          list.addRowList(rows);

          if (!isFinishing) {
            setTimeout(() => {
              this.addMeasurementRowsSnapshot(
                contractUid,
                measurementUid,
                list,
                onCreate,
                undefined,
                onFinish
              );
            }, 1000);
          }
        }
      };

      this.addMeasurementRowsSnapshot(
        contractUid,
        measurementUid,
        list,
        onCreate,
        undefined,
        onFinish
      );
    } catch (error) {
      console.error("Erro on subscribe snapshots to measurements rows", error);
    }
  }

  private addMeasurementRowsSnapshot(
    contractUid: string,
    measurementUid: string,
    rowsList: WBSModel<MeasurementRowModel>,
    onCreate: (data: MeasurementRowModel[], isFinishing: boolean) => void,
    onChange?: (data: MeasurementRowModel[]) => void,
    onFinish?: Function
  ) {
    const queryRef = MeasurementRowRepository.getMeasurementsRowsQuery(
      contractUid,
      measurementUid,
      rowsList.lastStructuredCode,
      maxRowsAtSnapshot
    );
    this.addSnapshot(rowsList, queryRef, onCreate, onChange, onFinish);
  }

  unsubscribeRows() {
    this.removeRowsSnapshots();
  }
}

export default new MeasurementRowService();
